/**
 * Supprimer une version de travail.
 *
 * Demande de Chris (24/08/2026) : « je ne sais pas où supprimer les versions modifiables,
 * par exemple si je ne veux pas garder celle de Régis ». Le geste vit ICI, à côté du choix
 * de la version et de sa création : c'est la même question — « laquelle je regarde », « j'en
 * veux une autre », « celle-là ne sert plus ».
 *
 * DEUX PARTIS PRIS.
 *
 *  - Le bouton n'apparaît que sur ce qui est supprimable — une version modifiable qui n'est
 *    pas la CE de suivi. Le devis figé, la CE initiale et la CE de suivi ne se suppriment
 *    jamais ; proposer un bouton qui échoue à tous les coups apprendrait juste à s'en méfier.
 *    Le serveur refuse de toute façon, et c'est lui qui fait foi : masquer un bouton n'est
 *    qu'un confort d'affichage.
 *  - La confirmation NOMME la version et annonce ce qui part. « Êtes-vous sûr ? » ne dit ni
 *    sur quoi on est sûr, ni de combien.
 */
import { useState } from "react";
import type { JSX } from "react";
import { supprimerVersion } from "./api";

type Etat =
  | { phase: "repos" }
  | { phase: "confirme" }
  | { phase: "envoi" }
  | { phase: "erreur"; raison: string };

export function SupprimerVersion({
  versionId,
  nom,
  onSupprimee,
}: {
  readonly versionId: string;
  /** Le nom affiché de la version — celui de la liste de choix, pour qu'on reconnaisse. */
  readonly nom: string;
  readonly onSupprimee: (bilan: string) => void;
}): JSX.Element | null {
  const [etat, setEtat] = useState<Etat>({ phase: "repos" });

  if (etat.phase === "repos") {
    return (
      <button
        type="button"
        className="lien"
        onClick={() => {
          setEtat({ phase: "confirme" });
        }}
      >
        Supprimer cette version…
      </button>
    );
  }

  const envoiEnCours = etat.phase === "envoi";

  const supprimer = (): void => {
    setEtat({ phase: "envoi" });
    supprimerVersion(versionId)
      .then((bilan) => {
        setEtat({ phase: "repos" });
        // Ce qui est parti, et — s'il y a lieu — ce qui a perdu la trace de son origine :
        // une conséquence qu'on ne verrait nulle part ailleurs si elle ne se disait pas ici.
        const detachees =
          bilan.detachees === 0
            ? ""
            : ` ${String(bilan.detachees)} version(s) qui en descendaient ne savent plus d'où elles viennent.`;
        onSupprimee(
          `Version supprimée : ${bilan.libelle} — ${String(bilan.postes)} poste(s), ` +
            `${String(bilan.lignes)} ligne(s), ${String(bilan.barres)} barre(s), ` +
            `${String(bilan.livraisons)} livraison(s), ${String(bilan.transports)} transport(s).` +
            detachees,
        );
      })
      .catch((erreur: unknown) => {
        setEtat({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  };

  return (
    <div className="supprimer-version">
      <p>
        Supprimer <strong>{nom}</strong> ? Son chiffrage et son planning partent avec elle, et
        c'est définitif.
      </p>
      <div className="supprimer-version__actions">
        <button type="button" className="danger" disabled={envoiEnCours} onClick={supprimer}>
          {envoiEnCours ? "Suppression…" : "Supprimer"}
        </button>
        <button
          type="button"
          disabled={envoiEnCours}
          onClick={() => {
            setEtat({ phase: "repos" });
          }}
        >
          Annuler
        </button>
      </div>
      {etat.phase === "erreur" && (
        <p className="alerte" role="alert">
          {etat.raison}
        </p>
      )}
    </div>
  );
}
